import { readEventStream } from "../sse";

/**
 * Asks the new Archi (customer-api-docs `apps/archi`), which reads the
 * Customer API with a pass traded for this same token — never with the token
 * itself. The lens travels as `?tenantId`, and Archi applies it to every read
 * it makes; the model never picks the tenant.
 */
export async function askArchi(opts: {
  token: string;
  archiUrl: string;
  /** The tenant the lens shows; omitted, Archi answers over everything the user sees. */
  tenantId?: number | null;
  body: {
    conversationId?: string | null;
    message: string;
    currentRoute?: string | null;
  };
  onEvent: (event: string, data: unknown) => void;
  signal?: AbortSignal;
}): Promise<void> {
  const { token, archiUrl, tenantId, body, onEvent, signal } = opts;
  const scope = tenantId
    ? `?tenantId=${encodeURIComponent(String(tenantId))}`
    : "";
  const resp = await fetch(`${archiUrl}/v1/messages${scope}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });
  await readEventStream(resp, onEvent);
}

/**
 * Whether this user may use Archi, decided by the Customer API when it trades
 * the pass: open to everyone since 2026-10-28 (CTX-1530), but the rule stays
 * on the server so it can change without a dashboard deploy. This
 * only decides whether the Archi button shows; every question is held to the
 * same rule on the server.
 *
 * An Archi without `/v1/access` predates the beta and lets everyone in, so a
 * 404 reads as allowed. Anything else that is not a yes hides the button: a
 * session or service Archi cannot use would fail on the first question anyway.
 */
export async function archiAccess(opts: {
  token: string;
  archiUrl: string;
  signal?: AbortSignal;
}): Promise<boolean> {
  const resp = await fetch(`${opts.archiUrl}/v1/access`, {
    headers: { Authorization: `Bearer ${opts.token}` },
    signal: opts.signal,
  });
  if (resp.status === 404) return true;
  if (!resp.ok) return false;
  const body = (await resp.json().catch(() => null)) as {
    enabled?: unknown;
  } | null;
  return body?.enabled === true;
}
